import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import * as moment from 'moment';

import './Calendar.scss';

const CalendarProgressBar = (props) => {
    const { value, text, classes, stripped, linkTo } = props;
    let progressClasses = classes;
    if (stripped) {
        progressClasses = `${progressClasses} progress-bar-striped`;
    }
    const height = 18;
    const bar = (
        <div className="progress" style={{ display: 'flex', height }}>
            <div
                className={`progress-bar ${progressClasses}`}
                role="progressbar"
                style={{ width: `${value}%` }}
                aria-valuenow={value}
                aria-valuemin="0"
                aria-valuemax="100"
            >
                {text}
            </div>
        </div>
    );
    if (linkTo) {
        return (
            <Link className="" to={linkTo}>
                {bar}
            </Link>
        );
    }
    return bar;
};

const Calendar = (props) => {
    const {
        loading,
        timelinesSchedule,
        timelinesPlayout,
        previousMonthCallback,
        nextMonthCallback,
        scheduleLayerId,
        playlistLayerId,
    } = props;

    const [currentMonth, setCurrentMonth] = useState(moment().startOf('month'));
    const [selectedDate, setSelectedDate] = useState(moment());

    const selectedBlock = selectedDate.format('YYYY-MM-DD');
    const minS = timelinesSchedule
        .filter((t) => t.blockId !== selectedBlock)
        .map((a) => a.eventsCount);
    const maxScheduleElements = minS.reduce((a, b) => (a > b ? a : b), 0);
    const minScheduleElements = minS.reduce((a, b) => (a > b ? b : a), maxScheduleElements);

    const maxScheduleDate = timelinesSchedule
        .map((s) => moment(s.blockId))
        .filter((d) => d.isValid())
        .reduce((a, b) => (a.diff(b, 'milliseconds') >= 0 ? a : b), moment());

    const minP = timelinesPlayout
        .filter((t) => t.blockId !== selectedBlock)
        .map((a) => a.eventsCount);
    const maxPlayoutElements = minP.reduce((a, b) => (a > b ? a : b), 0);
    const minPlayoutElements = minP.reduce((a, b) => (a > b ? b : a), maxPlayoutElements);
    const maxPlaylistDate = timelinesPlayout
        .map((s) => moment(s.blockId))
        .filter((d) => d.isValid())
        .reduce((a, b) => (a.diff(b, 'milliseconds') >= 0 ? a : b), moment());

    const renderHeader = () => {
        const dateFormat = 'MMMM YYYY';
        return (
            <div className="header row flex-middle">
                <div className="col col-start">
                    <div className="icon" onClick={prevMonth}>
                        &lt;
                    </div>
                </div>
                <div className="col col-center">
                    <span>{currentMonth.format(dateFormat)}</span>
                </div>
                <div className="col col-end" onClick={nextMonth}>
                    <div className="icon">&gt;</div>
                </div>
            </div>
        );
    };

    const renderDays = () => {
        const dateFormat = 'dddd';
        const days = [];
        const startDate = currentMonth.clone().startOf('week');
        for (let i = 0; i < 7; i += 1) {
            days.push(
                <div className="col col-center" key={i}>
                    {startDate
                        .clone()
                        .add(i, 'days')
                        .format(dateFormat)}
                </div>,
            );
        }
        return <div className="days row">{days}</div>;
    };

    const renderCells = () => {
        const monthStart = currentMonth.clone().startOf('month');
        const monthEnd = monthStart.clone().endOf('month');
        const startDate = monthStart.clone().startOf('week');
        const endDate = monthEnd.clone().endOf('week');

        const dateFormat = 'D';
        const rows = [];
        let days = [];
        let day = startDate;
        let formattedDate = '';
        while (day <= endDate) {
            for (let i = 0; i < 7; i += 1) {
                const dayClone = day.clone();
                formattedDate = dayClone.format(dateFormat);
                const schedule = timelinesSchedule.find(
                    (t) => t.blockId === dayClone.format('YYYY-MM-DD'),
                );
                let isPlaylistFailure = false;
                let isScheduleFailure = false;
                const playout = timelinesPlayout.find(
                    (t) => t.blockId === dayClone.format('YYYY-MM-DD'),
                );
                let scheduleCount = schedule ? schedule.eventsCount : -1;
                const scheduleLinkTo = schedule
                    ? `/channels/details/${schedule.channelId}/timelines/details/${schedule.id}/`
                    : null;
                let playoutCount = playout ? playout.eventsCount : -1;
                const playoutLinkTo = playout
                    ? `/channels/details/${playout.channelId}/timelines/details/${playout.id}/`
                    : null;
                let scheduleText = scheduleCount > 0 ? `Schedule (${scheduleCount})` : '';
                let playlistText = playoutCount > 0 ? `Playout (${playoutCount})` : '';

                let scheduleClasses = 'bg-info';
                if (dayClone.diff(maxScheduleDate, 'day') <= 0 && scheduleCount < 0) {
                    scheduleClasses = 'bg-danger';
                    scheduleCount = maxScheduleElements;
                    scheduleText = 'MISSING SCHEDULE';
                    isScheduleFailure = true;
                }
                let playlistClasses = 'bg-primary';
                if (dayClone.diff(maxPlaylistDate, 'day') < 0 && playoutCount < 0) {
                    playlistClasses = 'bg-danger';
                    playoutCount = maxPlayoutElements;
                    playlistText = 'MISSING PLAYLIST';
                    isPlaylistFailure = true;
                }
                if (loading) {
                    scheduleCount = maxScheduleElements;
                    playoutCount = maxPlayoutElements;
                    scheduleClasses = 'bg-secondary progress-bar-striped progress-bar-animated';
                    playlistClasses = 'bg-secondary progress-bar-striped progress-bar-animated';
                    scheduleText = '...';
                    playlistText = '...';
                }

                days.push(
                    <div
                        className={`col cell ${
                            !dayClone.isSame(monthStart, 'month')
                                ? 'disabled'
                                : dayClone.isSame(selectedDate, 'day')
                                ? 'selected'
                                : ''
                        }`}
                        key={dayClone}
                        // onClick={() => onDateClick(dayClone)}
                    >
                        <div className="number">{formattedDate}</div>
                        <div className="bg">{formattedDate}</div>
                        {dayClone.isSame(monthStart, 'month') ? (
                            <div className="progressBox" style={{ marginTop: 25 }}>
                                {scheduleLayerId ? (
                                    <CalendarProgressBar
                                        value={
                                            isScheduleFailure
                                                ? 100
                                                : Math.max(
                                                      0,
                                                      Math.min(
                                                          100,
                                                          (100.0 * scheduleCount) /
                                                              minScheduleElements,
                                                      ),
                                                  )
                                        }
                                        text={scheduleText}
                                        stripped
                                        classes={scheduleClasses}
                                        linkTo={scheduleLinkTo}
                                    />
                                ) : null}
                                {playlistLayerId ? (
                                    <CalendarProgressBar
                                        value={
                                            isPlaylistFailure
                                                ? 100
                                                : Math.max(
                                                      0,
                                                      Math.min(
                                                          100,
                                                          (100.0 * playoutCount) /
                                                              minPlayoutElements,
                                                      ),
                                                  )
                                        }
                                        text={playlistText}
                                        classes={playlistClasses}
                                        linkTo={playoutLinkTo}
                                    />
                                ) : null}
                            </div>
                        ) : null}
                    </div>,
                );
                day = day.clone().add(1, 'day');
            }
            rows.push(
                <div className="row" key={day}>
                    {days}
                </div>,
            );
            days = [];
        }

        return <div className="body">{rows}</div>;
    };

    const onDateClick = (day) => {
        console.log('Clicked day', day.toString());
        setSelectedDate(day);
    };

    const nextMonth = () => {
        nextMonthCallback();
        setCurrentMonth(currentMonth.clone().add(1, 'month'));
    };

    const prevMonth = () => {
        previousMonthCallback();
        setCurrentMonth(currentMonth.clone().add(-1, 'month'));
    };

    return (
        <div className="calendar">
            {renderHeader()}
            {renderDays()}
            {renderCells()}
        </div>
    );
};

export { Calendar };
